/**
 * Utility functions for generating calendar event
 */

import { DEFAULT_SESSION_DURATION_MINUTES } from "@src/constants";

export interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  startTime: Date;
  endTime: Date;
}

/**
 * Format date to iCalendar format (YYYYMMDDTHHMMSSZ)
 */
const formatICalDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Generate iCalendar (.ics) file content
 */
export const generateICalFile = (event: CalendarEvent): string => {
  const startDate = formatICalDate(event.startTime);
  const endDate = formatICalDate(event.endTime);
  const now = formatICalDate(new Date());

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Optum Behavioral Care//Appointment//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `DTSTAMP:${now}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description}`,
    `LOCATION:${event.location}`,
    "STATUS:CONFIRMED",
    "SEQUENCE:0",
    "BEGIN:VALARM",
    "TRIGGER:-PT24H",
    "ACTION:DISPLAY",
    `DESCRIPTION:Reminder: ${event.title}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  return icsContent;
};

/**
 * Download iCalendar file
 */
export const downloadICalFile = (
  event: CalendarEvent,
  filename: string = "appointment.ics",
): void => {
  const icsContent = generateICalFile(event);
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });

  // Check if on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

  if (isMobile) {
    // On mobile, create a data URI that opens in the default calendar app
    const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
    window.location.href = dataUri;
  } else {
    // On desktop, download the file
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }
};

/**
 * Add to calendar - always downloads .ics file
 */
export const addToCalendar = (event: CalendarEvent): void => {
  downloadICalFile(event, "appointment.ics");
};

/**
 * Parse appointment date and time string into Date objects
 * @param apptDate - Date string in format YYYY-MM-DD
 * @param apptTime - Time string in format HH:MM AM/PM
 * @returns Object with startTime and endTime Date objects, or null if parsing fails
 */
export const parseAppointmentDateTime = (
  apptDate: string | undefined,
  apptTime: string | undefined,
): { startTime: Date; endTime: Date } | null => {
  if (!apptDate || !apptTime) {
    return null;
  }

  try {
    // Parse date (format: YYYY-MM-DD) and time (format: HH:MM AM/PM)
    const [time, period] = apptTime.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    // Convert to 24-hour format
    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    const startTime = new Date(apptDate);
    startTime.setHours(hours, minutes, 0, 0);

    // End time matches the configured session duration.
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + DEFAULT_SESSION_DURATION_MINUTES);

    return { startTime, endTime };
  } catch {
    return null;
  }
};

/**
 * Create a calendar event from appointment details
 * @param appointmentDetails - Appointment details object
 * @param providerDetails - Provider details object
 * @returns CalendarEvent object or null if required data is missing
 */
export const createCalendarEventFromAppointment = (
  appointmentDetails: {
    apptDate?: string;
    apptTime?: string;
    careFormat?: string;
    apptLocation?: {
      clinic_name?: string;
      address_line_1?: string;
      address_line_2?: string;
      city?: string;
      state?: string;
      zip_code?: string;
    } | null;
  },
  providerDetails: {
    fullName?: string;
    providerType?: string;
    clinicianType?: string;
  },
): CalendarEvent | null => {
  const dateTime = parseAppointmentDateTime(
    appointmentDetails.apptDate,
    appointmentDetails.apptTime,
  );
  if (!dateTime) return null;

  const providerName = [providerDetails?.fullName, providerDetails?.providerType]
    .filter(Boolean)
    .join(", ");
  const sessionType = appointmentDetails?.careFormat === "in-person" ? "In-person" : "Virtual";

  let location = "";
  if (appointmentDetails?.careFormat === "in-person" && appointmentDetails?.apptLocation) {
    location = [
      appointmentDetails.apptLocation.clinic_name,
      appointmentDetails.apptLocation.address_line_1,
      appointmentDetails.apptLocation.address_line_2,
      appointmentDetails.apptLocation.city,
      `${appointmentDetails.apptLocation.state} ${appointmentDetails.apptLocation.zip_code}`,
    ]
      .filter(Boolean)
      .join(", ");
  } else {
    location = "Virtual Session";
  }

  return {
    title: `Therapy Session with ${providerName}`,
    description: `${sessionType} therapy session (${DEFAULT_SESSION_DURATION_MINUTES} minutes)\n\nProvider: ${providerName}\nType: ${providerDetails.clinicianType || "Therapy Session"}`,
    location,
    startTime: dateTime.startTime,
    endTime: dateTime.endTime,
  };
};
